import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import Footer from "../common/Footer";
import { getVendorWithProducts } from "../../services/store";
import "../../styles/Store.css";
import { toast } from "react-toastify";
import http from "../../lib/http";

/* ─── Helpers ──────────────────────────────────────────── */
const toHttps = (url) => (url ? url.replace("http://", "https://") : "");
const toCurrency = (n) => `$${Number(n || 0).toFixed(2)}`;
const getRating = (p) => Number(p?.average_rating ?? 0);
const getReviews = (p) => Number(p?.total_reviews ?? 0);
const getCatName = (p) => p?.category?.category_name ?? "";
const getImage = (p) => {
  const imgs = p?.product_images;
  if (Array.isArray(imgs) && imgs.length > 0) return toHttps(imgs[0]);
  return "";
};

// ── Resolve final price from new response shape ──────────
const resolvePrice = (p) => {
  const final = Number(p?.final_price ?? 0);
  const flash = Number(p?.flash_sale_price ?? 0);
  const discount = Number(p?.product_discount_price ?? 0);
  const regular = Number(p?.price ?? 0);
  if (final > 0) return final;
  if (flash > 0) return flash;
  if (discount > 0) return discount;
  return regular;
};

const hasAnyDiscount = (p) => {
  const display = resolvePrice(p);
  const original = Number(p?.price ?? 0);
  return display < original && original > 0;
};

const discountPct = (p) => {
  const display = resolvePrice(p);
  const original = Number(p?.price ?? 0);
  if (!hasAnyDiscount(p)) return 0;
  return Math.round(((original - display) / original) * 100);
};

/* ─── Fallback SVGs ─────────────────────────────────────── */
const STORE_FALLBACK =
  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120"%3E%3Crect fill="%23e8eef6" width="120" height="120" rx="16"/%3E%3Ctext x="60" y="72" text-anchor="middle" font-size="48"%3E🏪%3C/text%3E%3C/svg%3E';
const PRODUCT_FALLBACK =
  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"%3E%3Crect fill="%23e8eef6" width="200" height="200"/%3E%3Ctext x="100" y="115" text-anchor="middle" font-size="56"%3E📦%3C/text%3E%3C/svg%3E';

/* ─── Icons ─────────────────────────────────────────────── */
const SearchIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);
const CartIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="9" cy="21" r="1" />
    <circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
  </svg>
);
const ShopIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);
const PackageIcon = ({ size = 15 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" />
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>
);
const StarIcon = ({ filled = true }) => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill={filled ? "#f77321" : "none"}
    stroke="#f77321"
    strokeWidth="1.5"
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);
const FilterIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="4" y1="6" x2="20" y2="6" />
    <line x1="8" y1="12" x2="16" y2="12" />
    <line x1="11" y1="18" x2="13" y2="18" />
  </svg>
);
const CalendarIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const TagIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);
const CheckIcon = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const BoltIcon = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="currentColor"
    stroke="none"
  >
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

/* ─── Star rating display ───────────────────────────────── */
const StarRating = ({ rating, reviews }) => {
  const r = Number(rating || 0);
  return (
    <div className="st-rating-row">
      <div className="st-stars">
        {[1, 2, 3, 4, 5].map((i) => (
          <span
            key={i}
            className={i <= Math.round(r) ? "st-star-filled" : "st-star-empty"}
          >
            <StarIcon filled={i <= Math.round(r)} />
          </span>
        ))}
      </div>
      {r > 0 ? (
        <>
          <span className="st-rating-val">{r.toFixed(1)}</span>
          <span className="st-rating-count">
            ({reviews} review{reviews !== 1 ? "s" : ""})
          </span>
        </>
      ) : (
        <span className="st-rating-new">No reviews yet</span>
      )}
    </div>
  );
};

/* ─── Price badge — uses new response shape ─────────────── */
const PriceBadge = ({ product }) => {
  if (product.is_flash_sale) {
    return (
      <span className="st-discount-badge st-badge-flash">
        <BoltIcon /> Flash
      </span>
    );
  }
  const pct = discountPct(product);
  if (pct > 0) return <span className="st-discount-badge">{pct}% OFF</span>;
  return null;
};

/* ════════════════════════════════════════════════════════
   Store
════════════════════════════════════════════════════════ */
const Store = () => {
  const { unique_name } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState("products");
  const [storeData, setStoreData] = useState(null);
  const [products, setProducts] = useState([]);
  const [apiCategories, setApiCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [addingToCart, setAddingToCart] = useState(null);

  useEffect(() => {
    loadStoreData();
  }, [unique_name]);

  const loadStoreData = async () => {
    try {
      setLoading(true);
      // service returns response.data already (axios unwraps it)
      // backend shape: { message, data: { vendor, products } }
      const payload = await getVendorWithProducts(unique_name);
      console.log("[STORE] payload:", payload);

      const inner = payload?.data ?? payload;
      const vendor = inner?.vendor ?? null;
      const prods = inner?.products ?? inner?.data ?? [];

      setStoreData(vendor);
      setProducts(Array.isArray(prods) ? prods : []);

      const cats = [
        "All",
        ...new Set(
          (Array.isArray(prods) ? prods : [])
            .map((p) => getCatName(p))
            .filter(Boolean),
        ),
      ];
      setApiCategories(cats);
    } catch (e) {
      console.error("Error loading store data:", e);
      toast.error("Failed to load store information");
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = useCallback(
    async (e, product) => {
      e.stopPropagation();
      if (!user) {
        toast.info("Please log in to add items to cart");
        navigate("/login");
        return;
      }
      if (addingToCart === product.id) return;
      try {
        setAddingToCart(product.id);
        await http.post("/api/customer/cart", {
          product_id: product.id,
          quantity: 1,
        });
        toast.success(`${product.name} added to cart!`);
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to add to cart");
      } finally {
        setAddingToCart(null);
      }
    },
    [user, addingToCart, navigate],
  );

  const handleProductClick = (id) => navigate(`/product/${id}`);

  /* ── Filter + sort — uses resolvePrice for price filtering ── */
  const filteredProducts = products
    .filter((p) => {
      const matchSearch = p.name
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase());
      const matchCat =
        selectedCategory === "All" || getCatName(p) === selectedCategory;
      const price = resolvePrice(p); // ← use final price for filtering
      const matchMin = minPrice === "" || price >= Number(minPrice);
      const matchMax = maxPrice === "" || price <= Number(maxPrice);
      return matchSearch && matchCat && matchMin && matchMax;
    })
    .sort((a, b) => {
      if (sortBy === "price_asc") return resolvePrice(a) - resolvePrice(b);
      if (sortBy === "price_desc") return resolvePrice(b) - resolvePrice(a);
      if (sortBy === "rating") return getRating(b) - getRating(a);
      if (sortBy === "newest")
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      return 0;
    });

  if (loading) {
    return (
      <div className="st-screen">
        <div className="st-loading">
          <span className="st-spinner" />
          <p>Loading store…</p>
        </div>
        <Footer />
      </div>
    );
  }

  const storeDesc =
    storeData?.store_description || storeData?.description || "";
  const logoSrc =
    toHttps(storeData?.logo || storeData?.profile_image) || STORE_FALLBACK;

  return (
    <div className="st-screen">
      {/* ════ HERO ════ */}
      <div className="st-hero">
        <div className="st-hero-inner">
          {/* <div className="st-hero-logo-wrap"> */}
            <div className="st-hero-logo-icon">
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
            </div>
          {/* </div> */}
          <div className="st-hero-info">
            <h1 className="st-hero-name">
              {storeData?.unique_name || "Store"}
            </h1>
            <div className="st-hero-badges">
              {storeData?.approved && (
                <span className="st-verified-badge">
                  <CheckIcon /> Verified Seller
                </span>
              )}
              {storeData?.currency_preference && (
                <span className="st-currency-badge">
                  {storeData.currency_preference}
                </span>
              )}
            </div>
            <div className="st-hero-meta">
              <span className="st-hero-meta-item">
                <CalendarIcon />
                Joined{" "}
                {storeData?.created_at
                  ? new Date(storeData.created_at).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                    })
                  : "N/A"}
              </span>
              <span className="st-hero-meta-item">
                <PackageIcon size={12} /> {products.length} products
              </span>
            </div>
            {storeDesc && <p className="st-hero-desc">{storeDesc}</p>}
          </div>
        </div>
      </div>

      {/* ════ CONTENT ════ */}
      <div className="st-content">
        {/* Tabs + Search */}
        <div className="st-toolbar">
          <div className="st-tabs">
            <button
              className={`st-tab${activeTab === "store" ? " st-tab-active" : ""}`}
              onClick={() => setActiveTab("store")}
            >
              <ShopIcon /> Store
            </button>
            <button
              className={`st-tab${activeTab === "products" ? " st-tab-active" : ""}`}
              onClick={() => setActiveTab("products")}
            >
              <PackageIcon size={14} /> Products
              <span className="st-tab-count">{products.length}</span>
            </button>
          </div>
          <div className="st-search">
            <SearchIcon size={15} />
            <input
              type="text"
              className="st-search-input"
              placeholder="Search products…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className="st-search-clear"
                onClick={() => setSearchQuery("")}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        <div className="st-body">
          {/* ── Sidebar ── */}
          <aside className="st-sidebar">
            <div className="st-filter-card">
              <div className="st-filter-header">
                <FilterIcon /> Filters
              </div>

              <div className="st-filter-section">
                <p className="st-filter-title">
                  <TagIcon /> Category
                </p>
                <div className="st-filter-options">
                  {apiCategories.map((cat) => (
                    <label key={cat} className="st-checkbox">
                      <input
                        type="checkbox"
                        className="st-checkbox-input"
                        checked={selectedCategory === cat}
                        onChange={() => setSelectedCategory(cat)}
                      />
                      <span className="st-checkmark" />
                      <span className="st-checkbox-label">
                        {cat}
                        {cat !== "All" && (
                          <span className="st-cat-count">
                            {
                              products.filter((p) => getCatName(p) === cat)
                                .length
                            }
                          </span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="st-filter-section">
                <p className="st-filter-title">Price Range</p>
                <div className="st-price-range">
                  <div className="st-price-field">
                    <span className="st-price-field-label">Min</span>
                    <div className="st-price-input-wrap">
                      <span className="st-price-sym">$</span>
                      <input
                        type="number"
                        placeholder="0"
                        min="0"
                        className="st-price-input"
                        value={minPrice}
                        onChange={(e) => setMinPrice(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="st-price-sep" />
                  <div className="st-price-field">
                    <span className="st-price-field-label">Max</span>
                    <div className="st-price-input-wrap">
                      <span className="st-price-sym">$</span>
                      <input
                        type="number"
                        placeholder="Any"
                        min="0"
                        className="st-price-input"
                        value={maxPrice}
                        onChange={(e) => setMaxPrice(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                {(minPrice || maxPrice) && (
                  <button
                    className="st-clear-price"
                    onClick={() => {
                      setMinPrice("");
                      setMaxPrice("");
                    }}
                  >
                    ✕ Clear price filter
                  </button>
                )}
              </div>
            </div>
          </aside>

          {/* ── Products area ── */}
          <div className="st-products-area">
            <div className="st-products-header">
              <p className="st-products-count">
                <strong>{filteredProducts.length}</strong> of {products.length}{" "}
                products
                {selectedCategory !== "All" && (
                  <span className="st-active-filter">
                    {" "}
                    in <em>{selectedCategory}</em>
                  </span>
                )}
              </p>
              <div className="st-sort">
                <label className="st-sort-label">Sort:</label>
                <select
                  className="st-sort-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="">Default</option>
                  <option value="price_asc">Price: Low → High</option>
                  <option value="price_desc">Price: High → Low</option>
                  <option value="newest">Newest First</option>
                  <option value="rating">Best Rating</option>
                </select>
              </div>
            </div>

            {filteredProducts.length === 0 ? (
              <div className="st-no-products">
                <div className="st-no-products-icon">
                  <PackageIcon size={24} />
                </div>
                <p>No products found</p>
                <span>Try adjusting your search or filters</span>
              </div>
            ) : (
              <div className="st-products-grid">
                {filteredProducts.map((product) => {
                  const img = getImage(product);
                  const rating = getRating(product);
                  const reviews = getReviews(product);
                  const finalPrice = resolvePrice(product);
                  const origPrice = Number(product.price ?? 0);
                  const showOrig = origPrice > finalPrice && origPrice > 0;
                  const isFlash = !!product.is_flash_sale;
                  const isAdding = addingToCart === product.id;

                  return (
                    <div
                      key={product.id}
                      className="st-product-card"
                      onClick={() => handleProductClick(product.id)}
                    >
                      {/* Image */}
                      <div className="st-product-img-wrap">
                        <img
                          className="st-product-img"
                          src={img || PRODUCT_FALLBACK}
                          alt={product.name}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = PRODUCT_FALLBACK;
                          }}
                        />
                        <PriceBadge product={product} />
                        {getCatName(product) && (
                          <span className="st-product-cat-chip">
                            {getCatName(product)}
                          </span>
                        )}
                      </div>

                      {/* Info */}
                      <div className="st-product-info">
                        {product.brand && (
                          <span className="st-product-brand">
                            {product.brand}
                          </span>
                        )}
                        <h3 className="st-product-name">{product.name}</h3>
                        <StarRating rating={rating} reviews={reviews} />

                        {/* Price row — uses resolvePrice */}
                        <div className="st-product-footer">
                          <div className="st-price-group">
                            <span
                              className={`st-product-price${isFlash ? " st-price-flash" : ""}`}
                            >
                              {toCurrency(finalPrice)}
                            </span>
                            {showOrig && (
                              <span className="st-product-price-original">
                                {toCurrency(origPrice)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Store;
